import {Role} from "@/auth/oauth";
import Home from "@/views/public/Home.vue";
import Login from "../views/Login.vue";
import LoginPassword from "../views/LoginPassword.vue";
import RegisterAccount from "../views/RegisterAccount.vue";
import ActivateAccount from "../views/ActivateAccount.vue";
import OAuth from "../views/OAuth.vue";
import AccountPassword from "../views/AccountPassword.vue";
import Search from "../views/Search.vue";
import Groups from "../views/Groups.vue";
import ManageGroup from "../views/ManageGroup.vue";
import UserProfile from "../views/UserProfile.vue";
import MovingToEstonia from "../views/public/MovingToEstonia.vue";
import Programme from "../views/public/Programme.vue";
import Adaptation from "../views/public/Adaptation.vue";
import BasicModule from "../views/public/BasicModule.vue";
import LanguageModule from "../views/public/LanguageModule.vue";
import PasswordReset from "../views/PasswordReset.vue";
import UnsubscribeCourseRegistration from "../views/UnsubscribeCourseRegistration.vue";
import GDPR from "../views/public/GDPR.vue";
import Modules from "../views/Modules.vue";
import Schools from "../views/Schools.vue";
import ManageModule from "../views/ManageModule.vue";
import ManageSchool from "../views/ManageSchool.vue";
import FamilyModule from "../views/public/FamilyModule.vue";
import ProtectionModule from "../views/public/ProtectionModule.vue";
import WorkModule from "../views/public/WorkModule.vue";
import EntrepreneurshipModule from "../views/public/EntrepreneurshipModule.vue";
import Blog from "../views/public/Blog.vue";
import TemporaryProtectionModule from "../views/public/TemporaryProtectionModule.vue";
import TemporaryProtectionLangModule from "../views/public/TemporaryProtectionLangModule.vue";
import {createRouter, createWebHistory, type RouteLocation, type RouteRecordRaw} from "vue-router";
import Statistics from "@/views/Statistics.vue";
import UserProfileUpdatePage from "@/views/UserProfileUpdatePage.vue";
import type {I18n} from "vue-i18n";
import NotFound from "@/views/NotFound.vue";
import {AuthGuard} from "@/auth/guard";
import EventBus from "@/util/eventBus";
import InstitutionDiplomasPage from "@/views/InstitutionDiplomasPage.vue";
import ManageInstitution from "@/views/ManageInstitution.vue";
import type {AppDefinition} from "@/app";

export enum RouteName {
  "Home" = "Home",
  "MovingToEstonia" = "MovingToEstonia",
  "Programme" = "Programme",
  "Adaptation" = "Adaptation",
  "Login" = "Login",
  "LoginPassword" = "LoginPassword",
  "RegisterAccount" = "RegisterAccount",
  "ActivateAccount" = "ActivateAccount",
  "OAuth" = "OAuth",
  "Search" = "Search",
  "Modules" = "Modules",
  "ManageModule" = "ManageModule",
  "Schools" = "Schools",
  "ManageSchool" = "ManageSchool",
  "ManageInstitution" = "ManageInstitution",
  "Groups" = "Groups",
  "ManageGroup" = "ManageGroup",
  "PasswordReset" = "PasswordReset",
  "UnsubscribeCourseRegistration" = "UnsubscribeCourseRegistration",
  "AccountPassword" = "AccountPassword",
  "UserProfile" = "UserProfile",
  "Trainee" = "Trainee",
  "Statistics" = "Statistics",
  "InstitutionDiplomas" = "InstitutionDiplomas",
  "BasicModule" = "BasicModule",
  "GDPR" = "GDPR",
  "LanguageModule" = "LanguageModule",
  "FamilyModule" = "FamilyModule",
  "ProtectionModule" = "ProtectionModule",
  "WorkModule" = "WorkModule",
  "EntrepreneurshipModule" = "EntrepreneurshipModule",
  "Blog" = "Blog",
  "TemporaryProtectionModule" = "TemporaryProtectionModule",
  "TemporaryProtectionLangModule" = "TemporaryProtectionLangModule",
  "UserProfileUpdate" = "UserProfileUpdate",
  "NotFound" = "NotFound"
}

type RouteRecord = RouteRecordRaw & {
  name?: RouteName;
  children?: RouteRecord[];
}

const buildRoutes = (i18n: I18n<any>): RouteRecord[] => {
  const languagesPattern = i18n.global.availableLocales
      .map(lang => lang.toLowerCase())
      .join("|");

  return [
    {
      path: `/:lang(${languagesPattern})?`,
      children: [
        {
          path: "",
          name: RouteName.Home,
          component: Home,
        },
        {
          path: "moving-to-estonia",
          name: RouteName.MovingToEstonia,
          component: MovingToEstonia,
        },

        {
          path: "programme",
          name: RouteName.Programme,
          component: Programme,
        },
        {
          path: "adaptation",
          name: RouteName.Adaptation,
          component: Adaptation,
        },
        {
          path: "login",
          name: RouteName.Login,
          component: Login,
          meta: {
            anon: true,
          },
        },
        {
          path: "login/password",
          name: RouteName.LoginPassword,
          component: LoginPassword,
          meta: {
            anon: true,
          },
        },
        {
          path: "login/register",
          name: RouteName.RegisterAccount,
          component: RegisterAccount,
          meta: {
            anon: true,
          },
        },
        {
          path: "login/activate",
          name: RouteName.ActivateAccount,
          component: ActivateAccount,
        },
        {
          path: "openid/login/success",
          name: RouteName.OAuth,
          component: OAuth,
        },
        {
          path: "search",
          name: RouteName.Search,
          component: Search,
          meta: {
            roles: [Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN],
          },
        },
        {
          path: "modules",
          name: RouteName.Modules,
          component: Modules,
          meta: {
            roles: [Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.SCHOOL_MANAGER],
          },
        },
        {
          path: "modules/manage/:id",
          name: RouteName.ManageModule,
          component: ManageModule,
          props: true,
          meta: {
            roles: [Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.SCHOOL_MANAGER],
          },
        },

        {
          path: "schools",
          name: RouteName.Schools,
          component: Schools,
          meta: {
            roles: [Role.ADMIN],
          },
        },
        {
          path: "schools/manage/:id",
          name: RouteName.ManageSchool,
          component: ManageSchool,
          meta: {
            roles: [Role.ADMIN],
          },
        },
        {
          path: "insitutions/:id",
          name: RouteName.ManageInstitution,
          component: ManageInstitution,
          meta: {
            roles: [Role.ADMIN],
          },
        },
        {
          path: 'groups/:studentId?',
          name: RouteName.Groups,
          component: Groups,
          meta: {
            roles: [
              Role.TRAINEE,
              Role.TRAINER,
              Role.ADMIN,
              Role.VIEWER,
              Role.RVKT_ADMIN,
              Role.SCHOOL_MANAGER,
            ],
          },
        },
        {
          path: "account/password",
          name: RouteName.AccountPassword,
          component: AccountPassword,
          meta: {
            roles: [Role.ALL],
          },
        },
        {
          path: "groups/manage/:id",
          name: RouteName.ManageGroup,
          component: ManageGroup,
          meta: {
            roles: [Role.TRAINER, Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.SCHOOL_MANAGER],
          },
        },
        {
          path: "account/password-reset",
          name: RouteName.PasswordReset,
          component: PasswordReset,
        },
        {
          path: "account/unsubscribe-course-registration",
          name: RouteName.UnsubscribeCourseRegistration,
          component: UnsubscribeCourseRegistration,
        },
        {
          path: "account/profile/",
          name: RouteName.UserProfile,
          component: UserProfile,
          meta: {
            roles: [Role.ALL],
          },
        },
        {
          path: "trainee/:id",
          name: RouteName.Trainee,
          component: UserProfile,
          meta: {
            roles: [Role.TRAINER, Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN, Role.SCHOOL_MANAGER],
          },
        },
        {
          path: "statistics",
          name: RouteName.Statistics,
          component: Statistics,
          meta: {
            roles: [Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN],
          },
        },
        {
          path: "institution-diplomas",
          name: RouteName.InstitutionDiplomas,
          component: InstitutionDiplomasPage,
          meta: {
            roles: [Role.ADMIN, Role.VIEWER, Role.RVKT_ADMIN],
          }
        },
        {
          path: "programme/basic-module",
          name: RouteName.BasicModule,
          component: BasicModule,
        },
        {
          path: "gdpr",
          name: RouteName.GDPR,
          component: GDPR,
        },
        {
          path: "programme/language-module",
          name: RouteName.LanguageModule,
          component: LanguageModule,
        },
        {
          path: "programme/family-module",
          name: RouteName.FamilyModule,
          component: FamilyModule,
        },
        {
          path: "programme/protection-module",
          name: RouteName.ProtectionModule,
          component: ProtectionModule,
        },
        {
          path: "programme/work-module",
          name: RouteName.WorkModule,
          component: WorkModule,
        },
        {
          path: "programme/entrepreneurship-module",
          name: RouteName.EntrepreneurshipModule,
          component: EntrepreneurshipModule,
        },
        {
          path: "blog/:slug",
          name: RouteName.Blog,
          component: Blog,
          props: true,
        },
        {
          path: "programme/temporary-protection-module",
          name: RouteName.TemporaryProtectionModule,
          component: TemporaryProtectionModule,
        },
        {
          path: "programme/temporary-protection-lang-module",
          name: RouteName.TemporaryProtectionLangModule,
          component: TemporaryProtectionLangModule,
        },
        {
          path: "update-user-profile",
          name: RouteName.UserProfileUpdate,
          component: UserProfileUpdatePage,
          meta: {
            roles: [Role.ALL],
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      name: RouteName.NotFound,
      component: NotFound,
    },
  ];
}

const beforeEach = (app: AppDefinition, to: RouteLocation, i18n: I18n<any>) => {
  const langParamKey = "lang";
  const lang = to.params[langParamKey];

  if (!lang || typeof lang !== "string" || !i18n.global.availableLocales.includes(lang.toUpperCase())) {
    return AuthGuard(app, to);
  }

  EventBus.$emit("switch-locale", lang.toUpperCase());
  delete to.params[langParamKey];
  return to;
};

export const buildRouter = (app: AppDefinition, i18n: I18n<any>) => {
  const router = createRouter({
    history: createWebHistory(),
    routes: buildRoutes(i18n),
    scrollBehavior(_to, _from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      return { top: 0 };
    },
  });

  router.beforeEach((to) => {
    return beforeEach(app, to, i18n);
  });

  return router;
};
